import React, { useState, useEffect, useMemo, useRef } from 'react';
import bottomNavBg from '../../assets/icons/common/bottomNav.png';

// 메뉴 선택 바텀시트 — 매장 전체 메뉴 풀에서 메뉴를 골라 콜백으로 전달
// 클릭으로 선택/해제 토글. onSelect(menuKey, menu) = 신규 선택, onDeselect(menuKey) = 해제
// mode prop:
//   'grouped' (기본) — 카테고리 그룹 + 터치키 필터 (모드2 카테고리 배치용)
//   'flat'           — 모든 재고 평탄 (리뷰 메뉴 등록용)
const MenuPickerBottomSheet = ({ isOpen, onClose, storeId, onSelect, onDeselect, registeredKeys = [], title = '메뉴 추가', categoryName = '', mode = 'grouped', showFooter = false, onConfirm, readOnly = false }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [data, setData] = useState([]);   // flat: 메뉴 배열, grouped: 카테고리 배열
  const [isLoading, setIsLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [pendingKey, setPendingKey] = useState(null);
  const [catAnimating, setCatAnimating] = useState(null); // 카테고리 전체선택 버튼 펄스 애니메이션

  const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');
  const isFlat = mode === 'flat';

  // 첫 mount 시 깜빡임 방지: 더블 requestAnimationFrame.
  // 추가로 isOpen 시 history.pushState로 자기 entry를 쌓아 두고,
  // 닫힐 때 cleanup에서 정리 → 뒤로가기 1회는 picker만 닫고 부모는 유지.
  // onClose 가 parent 의 inline arrow function 이라 매 render 마다 새 함수 →
  // 그대로 dep 에 넣으면 useEffect 가 매번 re-run 되어 pushState 가 중첩됨
  // (첫 뒤로가기 안 먹는 원인). ref 로 stabilize.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(false);
      setIsVisible(true);
      setQuery('');
      window.history.pushState({ menuPicker: true }, '');

      // popstate (브라우저 뒤로가기) → onClose 호출
      const handlePop = () => {
        if (typeof onCloseRef.current === 'function') onCloseRef.current();
      };
      window.addEventListener('popstate', handlePop);

      let r2 = null;
      const r1 = requestAnimationFrame(() => {
        r2 = requestAnimationFrame(() => setIsAnimating(true));
      });
      return () => {
        cancelAnimationFrame(r1);
        if (r2) cancelAnimationFrame(r2);
        window.removeEventListener('popstate', handlePop);
        // popstate 로 닫혔으면 이미 history.back 된 상태 → menuPicker state 없음. skip.
        if (window.history.state?.menuPicker) {
          window.history.back();
        }
      };
    } else {
      setIsAnimating(false);
      const t = setTimeout(() => setIsVisible(false), 200);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // 메뉴 풀 로드 — mode에 따라 응답 형태가 다름
  //   grouped: { categories: [{category_code, category_name, menus: [...]}] }
  //   flat:    { menus: [...] }
  useEffect(() => {
    if (!isOpen || !storeId) return;
    let cancelled = false;
    setIsLoading(true);
    const params = [];
    if (isFlat) params.push('mode=flat');
    const url = `${API_BASE_URL}/api/stores/${storeId}/menu-pool${params.length ? '?' + params.join('&') : ''}`;
    fetch(url)
      .then(r => r.json())
      .then(d => {
        if (cancelled) return;
        if (!d.success) { setData([]); return; }
        setData(isFlat ? (d.menus || []) : (d.categories || []));
      })
      .catch(e => {
        if (!cancelled) {
          console.error('[메뉴 선택] 메뉴 풀 로드 실패:', e);
          setData([]);
        }
      })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, storeId, API_BASE_URL, isFlat]);

  const registeredSet = useMemo(() => new Set(registeredKeys), [registeredKeys]);

  // 검색 필터 (readOnly=매핑조회 모드면 등록된 메뉴만 표시)
  const filteredFlatMenus = useMemo(() => {
    if (!isFlat) return [];
    const q = query.trim().toLowerCase();
    let list = q ? data.filter(m => (m.menu_name || '').toLowerCase().includes(q)) : data;
    if (readOnly) list = list.filter(m => registeredSet.has(m.menu_key));
    return list;
  }, [isFlat, data, query, readOnly, registeredSet]);

  const filteredCategories = useMemo(() => {
    if (isFlat) return [];
    const q = query.trim().toLowerCase();
    let cats = data;
    if (q) {
      cats = data
        .map(cat => ({ ...cat, menus: (cat.menus || []).filter(m => (m.menu_name || '').toLowerCase().includes(q)) }))
        .filter(cat => cat.menus.length > 0);
    }
    if (readOnly) {
      cats = cats
        .map(cat => ({ ...cat, menus: (cat.menus || []).filter(m => registeredSet.has(m.menu_key)) }))
        .filter(cat => cat.menus.length > 0);
    }
    return cats;
  }, [isFlat, data, query, readOnly, registeredSet]);

  const totalMatched = useMemo(
    () => isFlat
      ? filteredFlatMenus.length
      : filteredCategories.reduce((sum, c) => sum + c.menus.length, 0),
    [isFlat, filteredFlatMenus, filteredCategories]
  );

  // 클릭 시 토글 — 이미 선택된 메뉴면 해제, 아니면 선택
  const handlePick = async (menu) => {
    if (readOnly) return; // 조회 전용 — 편집 불가
    const menuKey = menu.menu_key;
    if (pendingKey) return;
    const isRegistered = registeredSet.has(menuKey);
    setPendingKey(menuKey);
    try {
      if (isRegistered) {
        if (onDeselect) await onDeselect(menuKey);
      } else {
        if (onSelect) await onSelect(menuKey, menu);
      }
    } finally {
      setPendingKey(null);
    }
  };

  // 카테고리 전체선택/해제 토글 — 모두 선택돼 있으면 전체 해제, 아니면 미선택분 전체 선택
  const handleToggleCategory = (cat) => {
    if (readOnly) return; // 조회 전용 — 편집 불가
    if (pendingKey) return;
    const allSelected = cat.menus.every(m => registeredSet.has(m.menu_key));
    setCatAnimating(cat.category_code);
    setTimeout(() => setCatAnimating(null), 250);
    // await 없이 동기 호출 → React 배칭으로 카테고리 전체가 한 번에 선택/해제됨
    cat.menus.forEach(m => {
      const has = registeredSet.has(m.menu_key);
      if (allSelected) {
        if (has && onDeselect) onDeselect(m.menu_key);
      } else {
        if (!has && onSelect) onSelect(m.menu_key, m);
      }
    });
  };

  if (!isVisible) return null;

  return (
    <div className={`menu-picker-overlay ${isAnimating ? 'open' : ''}`} onClick={onClose}>
      <div
        className={`menu-picker-sheet ${isAnimating ? 'open' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="menu-picker-handle-area">
          <div className="menu-picker-handle" />
        </div>
        <div className="menu-picker-header">
          {categoryName ? (
            <span className="menu-picker-cat-chip">{categoryName}</span>
          ) : (
            <span className="menu-picker-title-text">{title}</span>
          )}
          <span className="menu-picker-selected-count">
            {registeredKeys.length}개 {readOnly ? '매핑됨' : '선택됨'}
          </span>
        </div>

        {!readOnly && (
        <div className="menu-picker-search-wrap">
          <div className="menu-picker-search-box">
            <svg className="menu-picker-search-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden>
              {/* iconify: material-symbols:search */}
              <path d="M15.5 14h-.79l-.28-.27a6.5 6.5 0 0 0 1.48-5.34c-.47-2.78-2.79-5-5.59-5.34a6.51 6.51 0 0 0-7.27 7.27c.34 2.8 2.56 5.12 5.34 5.59a6.5 6.5 0 0 0 5.34-1.48l.27.28v.79l4.25 4.25c.41.41 1.08.41 1.49 0c.41-.41.41-1.08 0-1.49zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5S14 7.01 14 9.5S11.99 14 9.5 14" fill="currentColor"/>
            </svg>
            <input
              className="menu-picker-search"
              type="text"
              placeholder="메뉴 검색"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {query && (
              <button className="menu-picker-search-clear" onClick={() => setQuery('')} aria-label="지우기">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="14" height="14">
                  <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12z" fill="currentColor"/>
                </svg>
              </button>
            )}
          </div>
        </div>
        )}

        <div className={`menu-picker-list ${readOnly ? 'readonly' : ''}`}>
          {isLoading && <div className="menu-picker-empty">불러오는 중...</div>}
          {!isLoading && totalMatched === 0 && (
            <div className="menu-picker-empty">
              {readOnly ? '매핑된 메뉴가 없습니다' : (query.trim() ? '검색 결과가 없습니다' : '등록 가능한 메뉴가 없습니다')}
            </div>
          )}
          {!isLoading && isFlat && filteredFlatMenus.map((m) => {
            const registered = registeredSet.has(m.menu_key);
            return (
              <div
                key={m.menu_key}
                className={`menu-picker-item ${registered ? 'selected' : ''}`}
                onClick={() => handlePick(m)}
              >
                <div className="menu-picker-name">{m.menu_name}</div>
                <div className="menu-picker-price">
                  {Number(m.price || 0).toLocaleString()}원
                </div>
              </div>
            );
          })}
          {!isLoading && !isFlat && filteredCategories.map((cat) => (
            <div key={cat.category_code} className="menu-picker-category">
              <div className="menu-picker-category-header" onClick={() => handleToggleCategory(cat)}>
                <span className="menu-picker-cat-toggle">
                  <span className={`cc ${cat.menus.every(m => registeredSet.has(m.menu_key)) ? 'selected' : ''} ${catAnimating === cat.category_code ? 'animating' : ''}`}>
                    <svg width="20" height="20" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <defs>
                        <mask id={`cc-mask-${cat.category_code}`}>
                          <rect x="-19" y="0" width="19" height="19" fill="white" className="cc-mask-rect"/>
                        </mask>
                      </defs>
                      <rect x="0" y="0" width="19" height="19" rx="9.5" fill="#FDCD27"/>
                      <rect x="0" y="0" width="19" height="19" rx="9.5" className="cc-white" fill="white"/>
                      <path className="cc-mark" transform="translate(1.71 1.71) scale(0.82)" d="M14.7583 5.27726C14.9131 5.45463 15 5.69518 15 5.94599C15 6.19681 14.9131 6.43735 14.7583 6.61473L8.5713 13.7044C8.48954 13.7981 8.39247 13.8725 8.28563 13.9232C8.17879 13.9739 8.06428 14 7.94864 14C7.83299 14 7.71848 13.9739 7.61165 13.9232C7.50481 13.8725 7.40774 13.7981 7.32597 13.7044L4.252 10.1826C4.17316 10.0953 4.11028 9.99096 4.06702 9.87556C4.02375 9.76016 4.00098 9.63604 4.00003 9.51044C3.99908 9.38485 4.01996 9.2603 4.06147 9.14405C4.10297 9.02781 4.16427 8.9222 4.24177 8.83339C4.31927 8.74458 4.41144 8.67434 4.51288 8.62678C4.61433 8.57922 4.72302 8.55529 4.83262 8.55638C4.94223 8.55747 5.05054 8.58357 5.15125 8.63314C5.25196 8.68271 5.34304 8.75477 5.41919 8.84511L7.94836 11.7433L13.5906 5.27726C13.6672 5.18936 13.7583 5.11963 13.8584 5.07206C13.9586 5.02449 14.066 5 14.1745 5C14.2829 5 14.3903 5.02449 14.4905 5.07206C14.5906 5.11963 14.6817 5.18936 14.7583 5.27726Z" fill="white" fillRule="evenodd" clipRule="evenodd" mask={`url(#cc-mask-${cat.category_code})`}/>
                      <rect x="1" y="1" width="17" height="17" rx="8.5" className="cc-border" stroke="#DDDDDD" strokeWidth={2} fill="none"/>
                    </svg>
                  </span>
                </span>
                <span className="menu-picker-category-name">{cat.category_name}</span>
                <span className="menu-picker-category-count">{cat.menus.length}개</span>
              </div>
              {cat.menus.map((m) => {
                const registered = registeredSet.has(m.menu_key);
                return (
                  <div
                    key={`${cat.category_code}-${m.menu_key}`}
                    className={`menu-picker-item ${registered ? 'selected' : ''}`}
                    onClick={() => handlePick(m)}
                  >
                    <div className="menu-picker-name">{m.menu_name}</div>
                    <div className="menu-picker-price">
                      {Number(m.price || 0).toLocaleString()}원
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        {showFooter && (
          <div className="menu-picker-footer">
            <button type="button" className="mp-footer-btn close" onClick={onClose}>닫기</button>
            <button
              type="button"
              className="mp-footer-btn apply"
              disabled={registeredKeys.length === 0}
              onClick={() => onConfirm && onConfirm()}
            >적용</button>
          </div>
        )}
      </div>

      <style jsx="true">{`
        .menu-picker-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0);
          z-index: 10050;
          transition: background 0.2s ease;
          overscroll-behavior: contain;
          touch-action: pan-y;
        }
        .menu-picker-overlay.open {
          background: rgba(0, 0, 0, 0.5);
        }

        .menu-picker-sheet {
          position: fixed;
          bottom: 0;
          left: 50%;
          transform: translate(-50%, 100%);
          width: 100%;
          max-width: 540px;
          height: 90%;
          background: white;
          border-radius: 24px 24px 0 0;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          overscroll-behavior: contain;
          touch-action: pan-y;
        }
        .menu-picker-sheet.open {
          transform: translate(-50%, 0);
        }

        .menu-picker-handle-area {
          display: flex;
          justify-content: center;
          padding: 12px 0 4px;
        }
        .menu-picker-handle {
          width: 40px;
          height: 4px;
          border-radius: 2px;
          background: #e0e0e0;
        }
        .menu-picker-header {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 36px 20px 16px;
        }
        /* adminDashboard.jsx 의 .category-badge 디자인 1:1 복사 (연필 아이콘 제외) */
        .menu-picker-cat-chip {
          background-color: #f1f1f1;
          color: #000000;
          padding: 4px 8px;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 500;
          display: inline-flex;
          align-items: center;
          line-height: 19px;
          text-align: center;
          white-space: nowrap;
          box-sizing: border-box;
          flex-shrink: 0;
          max-width: 70%;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .menu-picker-title-text {
          font-size: 16px;
          font-weight: 700;
          color: #222;
        }
        .menu-picker-selected-count {
          margin-left: auto;
          font-size: 13px;
          color: #888;
          font-weight: 500;
          flex-shrink: 0;
        }
        .menu-picker-search-wrap {
          padding: 0 20px 12px;
        }
        .menu-picker-search-box {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 0 14px;
          height: 44px;
          background: #f5f5f7;
          border: 1px solid transparent;
          border-radius: 12px;
          transition: border-color 0.15s ease, background 0.15s ease;
        }
        .menu-picker-search-box:focus-within {
          border-color: #FDCD27;
          background: #fff;
        }
        .menu-picker-search-icon {
          width: 18px;
          height: 18px;
          color: #9e9e9e;
          flex-shrink: 0;
        }
        .menu-picker-search-box:focus-within .menu-picker-search-icon {
          color: #555;
        }
        .menu-picker-search {
          flex: 1;
          min-width: 0;
          height: 100%;
          border: none;
          outline: none;
          background: transparent;
          font-size: 14px;
          color: #222;
          caret-color: #FDCD27;
        }
        .menu-picker-search::placeholder {
          color: #aaa;
        }
        .menu-picker-search-clear {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 20px;
          border: none;
          background: #d2d2d6;
          color: #fff;
          border-radius: 999px;
          cursor: pointer;
          padding: 0;
          flex-shrink: 0;
        }
        .menu-picker-list {
          flex: 1;
          overflow-y: auto;
          padding-bottom: calc(120 * 100vw / 360);
          padding: 0 20px 24px;
          -webkit-overflow-scrolling: touch;
        }
        .menu-picker-empty {
          text-align: center;
          padding: 40px 0;
          font-size: 13px;
          color: #aaa;
        }
        .menu-picker-category {
          margin-bottom: 14px;
        }
        .menu-picker-category-header {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 4px 8px;
          cursor: pointer;
          user-select: none;
        }
        .menu-picker-category-name {
          font-size: 16px;
          font-weight: 700;
          color: #222;
        }
        .menu-picker-category-count {
          font-size: 12px;
          color: #999;
        }
        .menu-picker-cat-toggle {
          background: none;
          border: none;
          padding: 2px;
          cursor: pointer;
          display: flex;
          align-items: center;
        }
        .menu-picker-cat-toggle .cc {
          width: 20px;
          height: 20px;
          position: relative;
          overflow: hidden;
          display: block;
        }
        .menu-picker-cat-toggle .cc svg {
          overflow: visible;
          display: block;
        }
        .menu-picker-cat-toggle .cc.animating {
          animation: ccScale 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }
        @keyframes ccScale {
          0% { transform: scale(1); }
          40% { transform: scale(0.9); }
          100% { transform: scale(1); }
        }
        .cc .cc-border {
          opacity: 1;
          transition: opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .cc .cc-white {
          transform: scale(1);
          transform-origin: center;
          transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .cc .cc-mask-rect {
          transform: translateX(0);
          transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1) 0.1s;
        }
        .cc .cc-mark { opacity: 1; }
        .cc.selected .cc-border { opacity: 0; }
        .cc.selected .cc-white { transform: scale(0); }
        .cc.selected .cc-mask-rect { transform: translateX(19px); }
        .menu-picker-footer {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: calc(100 * 100vw / 360);
          display: flex;
          justify-content: center;
          align-items: flex-end;
          padding-bottom: calc(16 * 100vw / 360);
          gap: calc(8 * 100vw / 360);
          pointer-events: none;
          z-index: 10;
        }
        .menu-picker-footer::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-image: url(${bottomNavBg});
          background-size: cover;
          background-position: center;
          pointer-events: none;
          z-index: 0;
        }
        .mp-footer-btn {
          position: relative;
          z-index: 2;
          pointer-events: auto;
          width: calc(164 * 100vw / 360);
          height: calc(50 * 100vw / 360);
          border-radius: calc(10 * 100vw / 360);
          border: none;
          font-size: calc(16 * 100vw / 360);
          font-weight: 700;
          cursor: pointer;
          transition: all 0.1s ease;
        }
        .mp-footer-btn.close {
          background: #F1F1F1;
          color: #959595;
        }
        .mp-footer-btn.apply {
          background: #FDCD27;
          color: #FFFFFF;
        }
        .mp-footer-btn.apply:disabled {
          background: #e0e0e0;
          color: #FFFFFF;
          cursor: not-allowed;
        }
        .mp-footer-btn:active {
          transform: scale(0.98);
        }
        @media screen and (min-width: 540px) {
          .menu-picker-footer { height: 150px; padding-bottom: 24px; gap: 12px; }
          .mp-footer-btn { width: 246px; height: 75px; border-radius: 15px; font-size: 24px; }
          .menu-picker-list { padding-bottom: 180px; }
        }
        .menu-picker-list.readonly .menu-picker-item,
        .menu-picker-list.readonly .menu-picker-category-header {
          cursor: default;
        }
        .menu-picker-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 14px 16px;
          margin-bottom: 8px;
          background: #fff;
          border: 1.5px solid #ececec;
          border-radius: 12px;
          cursor: pointer;
          transition: border-color 0.15s, background 0.15s;
        }
        .menu-picker-item:active {
          background: #f7f7f7;
        }
        .menu-picker-item.selected {
          border-color: #FDCD27;
          background: #FFFBE6;
        }
        .menu-picker-name {
          flex: 1;
          min-width: 0;
          font-size: 14px;
          font-weight: 600;
          color: #222;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .menu-picker-price {
          flex-shrink: 0;
          font-size: 13px;
          color: #666;
        }
      `}</style>
    </div>
  );
};

export default MenuPickerBottomSheet;
